import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@lib/authentication';
import { useErrorReporter } from '@lib/errorReporter';
import createGeolocationCacheStore from './internal/geolocationCache';
import useGeolocationAcquisition from './internal/useGeolocationAcquisition';
import useGeolocationPermission from './internal/useGeolocationPermission';
const GEOLOCATION_TIMEOUT_MS = 20000;
const GEOLOCATION_CACHE_TTL_MS = 60000;
const GEOLOCATION_MAX_POSITION_AGE_MS = 60000;
const GeolocationContext = createContext(undefined);
const GeolocationProvider = ({ children }) => {
    const errorReporter = useErrorReporter();
    const { isAuthenticated } = useAuth();
    const [geolocationStatus, setGeolocationStatus] = useState('getting');
    const [isGettingLocation, setIsGettingLocation] = useState(false);
    const cacheStore = useMemo(() => createGeolocationCacheStore(GEOLOCATION_CACHE_TTL_MS), []);
    const { getCurrentPosition } = useGeolocationAcquisition({
        cacheStore,
        errorReporter,
        timeoutMs: GEOLOCATION_TIMEOUT_MS,
        maximumAgeMs: GEOLOCATION_MAX_POSITION_AGE_MS,
        setGeolocationStatus,
        setIsGettingLocation
    });
    const { getGeolocationStatus } = useGeolocationPermission({
        errorReporter,
        setGeolocationStatus,
        setIsGettingLocation
    });
    const clearCachedPosition = useCallback(() => {
        cacheStore.clear();
    }, [cacheStore]);
    useEffect(() => {
        getGeolocationStatus();
        return () => {
            cacheStore.clear();
        };
    }, [cacheStore, getGeolocationStatus]);
    useEffect(() => {
        if (isAuthenticated)
            return;
        cacheStore.clear();
    }, [cacheStore, isAuthenticated]);
    const geolocationContext = useMemo(() => ({
        geolocationStatus,
        getCurrentPosition,
        getGeolocationStatus,
        isGettingLocation,
        clearCachedPosition
    }), [
        geolocationStatus,
        getCurrentPosition,
        getGeolocationStatus,
        isGettingLocation,
        clearCachedPosition
    ]);
    return (_jsx(GeolocationContext.Provider, { value: geolocationContext, children: children }));
};
export default GeolocationProvider;
export { GeolocationContext };
